/**
 * CurvePanel — Phase 1.7d
 * Faculty tool that lets you preview and apply a grade curve to all
 * finalized submissions of a rubric.
 * Three modes:
 *   - bump   (add raw points to every total)
 *   - scale  (multiply every total by a factor)
 *   - zscore (normalize to a target mean + std as % of max_total)
 */
import React, { useState } from 'react';
import { curvePreview, curveApply, curveClear } from '../lib/apiClient';

const Stat = ({ label, value }) => (
  <div className="px-3 py-2 rounded bg-gray-50 border border-gray-200">
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
    <div className="font-mono text-sm text-gray-900">{value}</div>
  </div>
);

const CurvePanel = ({ rubric, onApplied }) => {
  const [mode, setMode] = useState('bump');
  const [bump, setBump] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [tgtMean, setTgtMean] = useState(80);
  const [tgtStd, setTgtStd] = useState(8);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [applied, setApplied] = useState(false);

  const payload = () => ({
    mode,
    bump_points: Number(bump) || 0,
    scale_factor: Number(scale) || 1,
    target_mean_pct: Number(tgtMean) || 0,
    target_std_pct: Number(tgtStd) || 0,
  });

  const doPreview = async () => {
    setBusy(true); setErr(''); setApplied(false);
    try {
      const r = await curvePreview(rubric.id, payload());
      setPreview(r);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const doApply = async () => {
    setBusy(true); setErr('');
    try {
      const r = await curveApply(rubric.id, payload());
      setPreview({ ...preview, after: r.after });
      setApplied(true);
      onApplied && onApplied();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  };

  const doClear = async () => {
    if (!window.confirm('Remove curve from all submissions of this rubric?')) return;
    setBusy(true); setErr('');
    try {
      await curveClear(rubric.id);
      setPreview(null);
      setApplied(false);
      onApplied && onApplied();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Clear failed');
    } finally { setBusy(false); }
  };

  return (
    <section className="card p-5 mt-6" data-testid="curve-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="font-bold text-gray-900">Grade Curve</h2>
          <p className="text-xs text-gray-500">Preview, then apply. Curved totals appear alongside raw finals.</p>
        </div>
        {rubric.active_curve && (
          <span className="badge-published" data-testid="curve-active-pill" title={`Applied by ${rubric.active_curve.applied_by || 'faculty'}`}>
            ✓ Active: {rubric.active_curve.mode}
            {rubric.active_curve.mode === 'bump' && rubric.active_curve.bump_points ? ` +${rubric.active_curve.bump_points}` : ''}
            {rubric.active_curve.mode === 'scale' && rubric.active_curve.scale_factor ? ` ×${Number(rubric.active_curve.scale_factor).toFixed(2)}` : ''}
            {rubric.active_curve.mode === 'zscore' ? ` μ=${rubric.active_curve.target_mean_pct}% σ=${rubric.active_curve.target_std_pct}%` : ''}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Mode</label>
          <select className="input-field" value={mode} onChange={(e) => setMode(e.target.value)} data-testid="curve-mode-select">
            <option value="bump">Bump (add points)</option>
            <option value="scale">Scale (multiply)</option>
            <option value="zscore">Z-score (target mean + std)</option>
          </select>
        </div>

        {mode === 'bump' && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Bump (points)</label>
            <input
              type="range" min="0" max={rubric.total_max_points} step="0.5"
              value={bump} onChange={(e) => setBump(e.target.value)}
              className="w-48" data-testid="curve-bump-slider"
            />
            <div className="text-xs text-center font-mono">+{bump}</div>
          </div>
        )}
        {mode === 'scale' && (
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Scale factor</label>
            <input
              type="range" min="0.5" max="2.0" step="0.05"
              value={scale} onChange={(e) => setScale(e.target.value)}
              className="w-48" data-testid="curve-scale-slider"
            />
            <div className="text-xs text-center font-mono">×{Number(scale).toFixed(2)}</div>
          </div>
        )}
        {mode === 'zscore' && (
          <>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Target mean (% of max)</label>
              <input
                type="range" min="50" max="100" step="1"
                value={tgtMean} onChange={(e) => setTgtMean(e.target.value)}
                className="w-40" data-testid="curve-mean-slider"
              />
              <div className="text-xs text-center font-mono">{tgtMean}%</div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Target std (% of max)</label>
              <input
                type="range" min="2" max="25" step="1"
                value={tgtStd} onChange={(e) => setTgtStd(e.target.value)}
                className="w-40" data-testid="curve-std-slider"
              />
              <div className="text-xs text-center font-mono">{tgtStd}%</div>
            </div>
          </>
        )}

        <button onClick={doPreview} disabled={busy} className="btn-secondary text-sm" data-testid="curve-preview-btn">
          {busy ? 'Working…' : 'Preview'}
        </button>
        <button onClick={doApply} disabled={busy || !preview} className="btn-primary text-sm" data-testid="curve-apply-btn">
          Apply to all
        </button>
        {rubric.active_curve && (
          <button onClick={doClear} disabled={busy} className="text-sm text-red-600 hover:underline" data-testid="curve-clear-btn">
            Clear curve
          </button>
        )}
      </div>

      {err && <div className="mt-2 p-2 rounded border border-red-200 bg-red-50 text-red-700 text-sm">{err}</div>}
      {applied && <div className="mt-2 p-2 rounded border border-emerald-200 bg-emerald-50 text-emerald-700 text-sm" data-testid="curve-applied-banner">Curve applied to all submissions.</div>}

      {preview && (
        <div className="mt-4" data-testid="curve-preview-result">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <Stat label="N" value={preview.before.n} />
            <Stat label="Before mean" value={preview.before.mean} />
            <Stat label="After mean" value={preview.after.mean} />
            <Stat label="Before std" value={preview.before.std} />
            <Stat label="After std" value={preview.after.std} />
          </div>
          {preview.rows.length > 0 && (
            <div className="overflow-x-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-gray-600">Student</th>
                    <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-gray-600">Final</th>
                    <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-gray-600">Curved</th>
                    <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-gray-600">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.submission_id} className="border-b" data-testid={`curve-row-${r.submission_id}`}>
                      <td className="px-3 py-2">{r.student_name || r.student_email}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.final_total}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold">{r.curved_total}</td>
                      <td className={`px-3 py-2 text-right font-mono ${r.delta > 0 ? 'text-emerald-700' : r.delta < 0 ? 'text-red-600' : 'text-gray-500'}`}>
                        {r.delta > 0 ? '+' : ''}{r.delta}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default CurvePanel;
